import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  InputAdornment,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { lightTheme } from "../../styles/theme";
import { getAvatarColor } from "../../utils/avatarUtils";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
} from "../../styles/managementShared";

export interface Invitation {
  invitationId: string;
  email?: string;
  phoneNumber?: string;
  role: string;
  accessLegacyMessages: boolean;
  legacyAccessDays?: number;
  memberAlias?: string;
  status: "pending" | "accepted" | "expired";
  invitedBy: string;
  invitedAt: string;
}

interface InvitationListProps {
  invitations: Invitation[];
  isMobile: boolean;
  isLoading: boolean;
  onCancelInvitation?: (invitationId: string) => void;
  onAliasChange?: (invitationId: string, alias: string) => Promise<void> | void;
  savingAliasInvitationId?: string | null;
  isCanceling?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  emptyExtraContent?: React.ReactNode;
}

const createStyles = () => ({
  headerCell: {
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase" as const,
    fontSize: "0.75rem",
    letterSpacing: "0.5px",
  },
  mobileCard: {
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    flexDirection: "row" as const,
    gap: 2,
    minHeight: 56,
    position: "relative" as const,
  },
  mobileCardContent: {
    flex: 1,
    minWidth: 0,
  },
  mobileCardName: {
    fontSize: "0.875rem",
  },
  mobileCardDate: {
    fontSize: "0.625rem",
  },
  mobileDateContainer: {
    width: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: 1,
  },
});

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getContactDisplay = (invitation: Invitation): string => {
  return invitation.email || invitation.phoneNumber || "N/A";
};

const getAccessLabel = (invitation: Invitation): string => {
  if (invitation.legacyAccessDays === 7) return "7 days";
  if (invitation.accessLegacyMessages) return "All";
  return "None";
};

const getInvitationInitials = (invitation: Invitation): string => {
  const alias = invitation.memberAlias?.trim();
  if (alias) {
    const parts = alias.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return alias.substring(0, 2).toUpperCase();
  }
  const contact = getContactDisplay(invitation);
  if (contact === "N/A") return "?";
  const parts = contact.split("@")[0].split(/[.+\-_]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return contact.substring(0, 2).toUpperCase();
};

const InvitationList: React.FC<InvitationListProps> = React.memo(
  ({
    invitations,
    isMobile,
    isLoading,
    onCancelInvitation,
    onAliasChange,
    savingAliasInvitationId = null,
    isCanceling,
    emptyTitle,
    emptyDescription,
    emptyActionLabel,
    onEmptyAction,
    emptyExtraContent,
  }) => {
    const styles = useMemo(() => createStyles(), []);
    const [aliasDrafts, setAliasDrafts] = useState<Record<string, string>>({});
    const aliasDraftsRef = useRef(aliasDrafts);
    const aliasDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
      {}
    );
    aliasDraftsRef.current = aliasDrafts;

    useEffect(() => {
      setAliasDrafts((prev) => {
        const next: Record<string, string> = {};
        for (const invitation of invitations) {
          next[invitation.invitationId] =
            prev[invitation.invitationId] !== undefined
              ? prev[invitation.invitationId]
              : invitation.memberAlias ?? "";
        }
        return next;
      });
    }, [invitations]);

    useEffect(() => {
      const pendingTimers = aliasDebounceRef.current;
      return () => {
        Object.values(pendingTimers).forEach(clearTimeout);
      };
    }, []);

    const persistAlias = async (invitationId: string) => {
      if (!onAliasChange) return;
      const invitation = invitations.find(
        (inv) => inv.invitationId === invitationId
      );
      if (!invitation) return;
      const next = (aliasDraftsRef.current[invitationId] ?? "").trim();
      const current = (invitation.memberAlias ?? "").trim();
      if (next === current) return;
      await onAliasChange(invitationId, next);
    };

    const scheduleAliasSave = (invitationId: string) => {
      if (!onAliasChange) return;
      if (aliasDebounceRef.current[invitationId]) {
        clearTimeout(aliasDebounceRef.current[invitationId]);
      }
      aliasDebounceRef.current[invitationId] = setTimeout(() => {
        delete aliasDebounceRef.current[invitationId];
        void persistAlias(invitationId);
      }, 600);
    };

    const flushAliasSave = (invitationId: string) => {
      if (aliasDebounceRef.current[invitationId]) {
        clearTimeout(aliasDebounceRef.current[invitationId]);
        delete aliasDebounceRef.current[invitationId];
      }
      void persistAlias(invitationId);
    };

    const renderAliasField = (invitation: Invitation, fullWidth = false) => {
      if (!onAliasChange) {
        const alias = invitation.memberAlias?.trim();
        return alias ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {alias}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        );
      }

      return (
        <TextField
          size="small"
          placeholder="e.g. John's Mom"
          value={aliasDrafts[invitation.invitationId] ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            setAliasDrafts((prev) => ({
              ...prev,
              [invitation.invitationId]: value,
            }));
            scheduleAliasSave(invitation.invitationId);
          }}
          onBlur={() => flushAliasSave(invitation.invitationId)}
          inputProps={{ maxLength: 60 }}
          sx={{
            width: fullWidth ? "100%" : { xs: "100%", sm: 180 },
            maxWidth: "100%",
            mt: fullWidth ? 0.75 : 0,
          }}
          InputProps={{
            sx: {
              borderRadius: 1.5,
              bgcolor: "background.paper",
              fontSize: "0.8125rem",
            },
            endAdornment:
              savingAliasInvitationId === invitation.invitationId ? (
                <InputAdornment position="end">
                  <CircularProgress color="inherit" size={16} thickness={5} />
                </InputAdornment>
              ) : undefined,
          }}
        />
      );
    };

    if (isLoading) {
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading invitations...
        </Typography>
      );
    }

    if (invitations.length === 0) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "text.secondary",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            {emptyTitle ?? "No pending invitations"}
          </Typography>
          <Typography variant="body2">
            {emptyDescription ??
              "All invited users have created accounts or no invitations have been sent yet."}
          </Typography>
          {emptyExtraContent}
          {emptyActionLabel && onEmptyAction ? (
            <Button
              variant="contained"
              onClick={onEmptyAction}
              sx={{ mt: 2.5 }}
            >
              {emptyActionLabel}
            </Button>
          ) : null}
        </Box>
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {invitations.map((invitation) => {
            const contact = getContactDisplay(invitation);
            const accessLabel = getAccessLabel(invitation);
            const alias = invitation.memberAlias?.trim();

            return (
              <Paper
                key={invitation.invitationId}
                elevation={0}
                sx={{
                  ...styles.mobileCard,
                  p: 1.5,
                  borderRadius: 1.5,
                  alignItems: "flex-start",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: MANAGEMENT_AVATAR_SIZE,
                    height: MANAGEMENT_AVATAR_SIZE,
                    backgroundColor: getAvatarColor(
                      alias || contact,
                      lightTheme
                    ),
                    color: "common.white",
                    fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                    fontWeight: 600,
                    mr: 1,
                    mt: 0.25,
                  }}
                >
                  {getInvitationInitials(invitation)}
                </Avatar>
                <Box sx={styles.mobileCardContent}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    noWrap
                    color="text.primary"
                    sx={styles.mobileCardName}
                  >
                    {contact}
                  </Typography>
                  {renderAliasField(invitation, true)}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={`Legacy: ${accessLabel}`}
                      size="small"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                    <Chip
                      label="Pending"
                      size="small"
                      color="warning"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={styles.mobileCardDate}
                  >
                    {formatDate(invitation.invitedAt)}
                  </Typography>
                  {onCancelInvitation && (
                    <Tooltip title="Cancel invitation">
                      <IconButton
                        size="small"
                        onClick={() =>
                          onCancelInvitation(invitation.invitationId)
                        }
                        disabled={isCanceling}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.6fr) minmax(160px, 1.4fr) 1.1fr 1.1fr 0.9fr 0.8fr",
            gap: 2,
            px: 3,
            py: 1,
            mb: 1,
          }}
        >
          <Typography sx={styles.headerCell}>Contact</Typography>
          <Typography sx={styles.headerCell}>Alias</Typography>
          <Typography sx={styles.headerCell}>Date Invited</Typography>
          <Typography sx={styles.headerCell}>Legacy Access</Typography>
          <Typography sx={styles.headerCell}>Status</Typography>
          <Typography sx={styles.headerCell}>Actions</Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {invitations.map((invitation) => {
            const contact = getContactDisplay(invitation);
            const accessLabel = getAccessLabel(invitation);
            const alias = invitation.memberAlias?.trim();

            return (
              <Paper
                key={invitation.invitationId}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(0, 1.6fr) minmax(160px, 1.4fr) 1.1fr 1.1fr 0.9fr 0.8fr",
                    gap: 2,
                    px: 3,
                    py: 2,
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: MANAGEMENT_AVATAR_SIZE,
                        height: MANAGEMENT_AVATAR_SIZE,
                        backgroundColor: getAvatarColor(
                          alias || contact,
                          lightTheme
                        ),
                        color: "common.white",
                        fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {getInvitationInitials(invitation)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontWeight: 500,
                          fontSize: (theme) => theme.typography.body2.fontSize,
                        }}
                      >
                        {alias || contact}
                      </Typography>
                      {alias ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block" }}
                        >
                          {contact}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Box>{renderAliasField(invitation)}</Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    {formatDate(invitation.invitedAt)}
                  </Typography>

                  <Box>
                    <Chip
                      label={`Legacy: ${accessLabel}`}
                      size="small"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  </Box>

                  <Box>
                    <Chip
                      label="Pending"
                      size="small"
                      color="warning"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {onCancelInvitation && (
                      <Tooltip title="Cancel invitation">
                        <IconButton
                          size="small"
                          onClick={() =>
                            onCancelInvitation(invitation.invitationId)
                          }
                          disabled={isCanceling}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }
);

InvitationList.displayName = "InvitationList";

export default InvitationList;
